import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
// Interfaces

import { assetMimeTypes } from '@cube3/state/types';
// Hooks
import {
  useCreateResource__ALPHA,
  useMappedId__ALPHA
} from '@cube3/state/src/redux/components/Hooks/useCreateResource';

// State
import { ReviewViewContext } from '../../ReviewTool/Context/reviewViewContext';
import {
  ReviewAssetViewerContext,
  ReviewToolContext
} from '../Context/reviewToolContext';

// UI
import CommentField from '@cube3/cubicle/src/core/molecules/ReviewTools/CommentField';
import { ReviewRatingSettings } from './ReviewRatingSettings';
import { ReviewCommentsList } from './ReviewCommentsList';
import { uuidv4 } from '@cube3/state/src/utils/uuid';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import {
  ReviewCommentsFilterContext,
  ReviewCommentsFilterProvider
} from '../Context/reviewCommentsFilterContext';
import { matchPath, useLocation } from 'react-router';
import {
  ReviewRouteParams,
  urlStructureReviewLinkComment
} from '../../../routing/routingPaths';
import { isMobile } from 'react-device-detect';


interface CommentFieldSmartProps {
  onMobileDraw?: () => void;
  onCancel?: () => void;
  onPost?: () => void;
  //when the values come from the add comment modal (mobile)
  isModal?: boolean;
}


export const ReviewPanel = () => {

  return (
    <div
      style={{
        boxSizing: 'border-box',
        height: 'calc(100% - 64px)',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}
    >
      {/* Rating */}
      <ReviewRatingSettings />
      <ReviewCommentsFilterProvider>
        {/** Comment field */}
        <CommentFieldSmart />
        <ReviewCommentsList />
      </ReviewCommentsFilterProvider>
    </div>
  );
};


export const CommentFieldSmart = (props: CommentFieldSmartProps) => {

  const { onMobileDraw, onCancel, onPost, isModal } = props;
  const { activeAsset, reviewLink, email, userImage } =
    useContext(ReviewViewContext);
  const { filter, setFilter } = useContext(ReviewCommentsFilterContext);

  const {
    drawnSvg,
    drawingBarToggled,
    player,
    playing,
    activeTemplate,
    setDrawnSvg,
    toggleDrawingBar,
    setActiveComment
  } = useContext(ReviewToolContext);



  const { timestamp } = useContext(ReviewAssetViewerContext);
  const { setOpenCommentBar } = useContext(ReviewViewContext);

  const [tempId, setTempId] = useState(initialTempId);
  // CommentField calls onToggleDrawingBar right after onSend when a drawing is attached;
  // in the modal that toggle must not navigate the modal stack again (it would close the "Comment Saved" notice)
  const postedWithDrawingRef = useRef(false);

  const [createComment] = useCreateResource__ALPHA({
    resourceType: 'comment',
    cacheInvalidator: useCallback(
      (r) => [
        r,
        {
          type: 'content-tree-node',
          id: activeAsset?.id,
          relationship: 'comments'
        }
      ],
      [activeAsset]
    )
  });

  const onSendComment = useCallback(
    (message: string, timestamp?: number) => {
      createComment({
        temporaryId: tempId,
        type: 'comment',
        message: message,
        drawing_svg: drawingBarToggled ? drawnSvg : '',
        author_name: email,
        recipient_name: email,
        recipient_image: userImage,
        start: timestamp,
        //  end: timestampChecked ? timestamp + 1 : undefined,
        relationships: {
          node: {
            data: {
              id: activeAsset?.id,
              type: 'node'
            }
          }
        }
      });
      // when adding a new comment in the “resolved comments” view, the comments filter should switch to “all comments”
      // so the new comment will be visible
      if (filter === 'resolve') {
        setFilter('all');
      }
      setDrawnSvg('');
      if (isModal) {
        //if the post comes from the add comment modal, close the modal
        postedWithDrawingRef.current = drawingBarToggled;
        onPost();
      }
    },
    [
      setFilter,
      setDrawnSvg,
      createComment,
      filter,
      drawnSvg,
      drawingBarToggled,
      reviewLink,
      activeAsset,
      tempId,
      email,
      userImage
    ]
  );

  // after the comment is created, set it as active
  const id = useMappedId__ALPHA(tempId);
  const { resource: newComment } = useResource__ALPHA({
    resourceId: id,
    resourceType: 'comment'
  });
  useEffect(() => {
    if (newComment) {
      setActiveComment?.(newComment);
      setTempId(uuidv4());
    }
  }, [newComment]);

  const assetType =
    activeAsset?.mime_type === assetMimeTypes.VIDEO
      ? 'video'
      : activeAsset?.mime_type === assetMimeTypes.IMAGE
        ? 'image'
        : undefined;

  const onToggleDrawingBar = useCallback(() => {
    if (postedWithDrawingRef.current) {
      // comment was just posted from the modal: only close the drawing bar, onPost already handled the modals
      postedWithDrawingRef.current = false;
      toggleDrawingBar(false);
      return;
    }
    // make “draw” button pause the video
    if (player && player.state.playing) {
      player.playPause(false);
    }
    toggleDrawingBar();

    if (isMobile) {
      if (isModal) {
        onMobileDraw();
      } else {
        //close comment section
        setOpenCommentBar(false);
      }
    }

    if (drawingBarToggled) {
      //close modal and clean drawing
      if (isModal) {
        onCancel();
        setDrawnSvg('');
      } else {
        //clean drawing after "Cancel" from commentField
        setDrawnSvg('');
      }
    }

  }, [player, drawingBarToggled, toggleDrawingBar]);


  //same behavior for desktop and mobile, in AssetTileSmart
  // clear cached drawing_svg when switching between assets
  // useEffect(() => {
  //   if (drawnSvg) {
  //     setDrawnSvg('');
  //   }
  //   // exit drawing mode when switching items
  //   if (drawingBarToggled) {
  //     toggleDrawingBar();
  //   }
  // }, [activeAsset]);

  // if the url contains a comment id, we want to set the filter to 'all' on the first mount,
  // so that we could still see the resolved comments
  const location = useLocation();
  const matchedCommentId = matchPath<ReviewRouteParams>(
    location.pathname,
    urlStructureReviewLinkComment
  )?.params?.commentId;

  useEffect(() => {
    if (matchedCommentId) {
      setFilter('all');
    }
  }, []);


  return (
    <CommentField
      isDrawable={!!assetType && !activeTemplate}
      assetType={assetType}
      timestamp={timestamp}
      drawingBarToggled={drawingBarToggled}
      onToggleDrawingBar={activeTemplate ? null : onToggleDrawingBar}
      onSend={onSendComment}
      reviewer={email}
      reviewerImage={userImage}
      playing={playing}
      playPause={player?.playPause}
      drawnSvg={drawnSvg}
    />
  );
};

const initialTempId = () => uuidv4();
